"use client";
import { motion } from "framer-motion";
import { User, Mail, Briefcase, Globe, TrendingUp } from "lucide-react";
import Header from "../../../Components/Header";
import Footer from "../../../Components/Footer";

export default function DetailsPage() {
  // 🔹 Mock User Data (replace later with API)
  const user = {
    name: "Aman Sharma",
    email: "aman.sharma@example.com",
    role: "Software Engineer",
    location: "Bangalore, India",
    linkedin: "https://linkedin.com/in/aman-sharma",
    resume: "https://example.com/resume.pdf",
    joined: "Jan 2024",
  };

  // 🔹 Mock Growth Data
  const growthData = {
    before: {
      image:
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&auto=format&fit=crop",
      comment:
        "Before optimization, Aman’s LinkedIn profile lacked clarity and focus. His resume was not ATS-friendly, and recruiter engagement was low.",
    },
    after: {
      image:
        "https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=800&auto=format&fit=crop",
      comment:
        "After our revamp, Aman’s resume was fully optimized with targeted keywords, and his LinkedIn profile started getting 3x more recruiter messages.",
    },
    metrics: {
      resumeScore: "45 → 92",
      linkedinViews: "80 → 250+",
      recruiterMessages: "5 → 18",
    },
  };

  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-700 text-white py-28 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mx-auto px-6"
        >
          <h1 className="text-5xl font-extrabold mb-4">
            {user.name}’s Growth Journey 🚀
          </h1>
          <p className="text-lg text-indigo-100">
            A real example of how small improvements make a big impact on your
            career visibility and recruiter reach.
          </p>
        </motion.div>
      </section>

      {/* User Details Section */}
      <section className="py-20 px-6 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12"
        >
          <h2 className="text-3xl font-bold mb-6 text-indigo-700 flex items-center gap-3">
            <User className="text-indigo-600" /> User Details
          </h2>

          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <p className="font-semibold">Full Name:</p>
              <p>{user.name}</p>
            </div>
            <div>
              <p className="font-semibold">Email:</p>
              <p>{user.email}</p>
            </div>
            <div>
              <p className="font-semibold">Role:</p>
              <p>{user.role}</p>
            </div>
            <div>
              <p className="font-semibold">Location:</p>
              <p>{user.location}</p>
            </div>
            <div>
              <p className="font-semibold">LinkedIn:</p>
              <a
                href={user.linkedin}
                className="text-indigo-600 hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                View Profile
              </a>
            </div>
            <div>
              <p className="font-semibold">Resume:</p>
              <a
                href={user.resume}
                className="text-indigo-600 hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                Download Resume
              </a>
            </div>
            <div>
              <p className="font-semibold">Joined:</p>
              <p>{user.joined}</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* My Growth Section */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-bold mb-10 text-center text-indigo-700"
        >
          My Growth 📈
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-10">
          {/* Before */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <img
              src={growthData.before.image}
              alt="Before Optimization"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Before Optimization
              </h3>
              <p className="text-gray-600">{growthData.before.comment}</p>
            </div>
          </motion.div>

          {/* After */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <img
              src={growthData.after.image}
              alt="After Optimization"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                After Optimization
              </h3>
              <p className="text-gray-600">{growthData.after.comment}</p>
            </div>
          </motion.div>
        </div>

        {/* Key Results */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-16 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-8 shadow-inner text-center"
        >
          <h3 className="text-2xl font-bold text-indigo-700 mb-6 flex justify-center items-center gap-2">
            <TrendingUp /> Key Growth Metrics
          </h3>

          <div className="grid md:grid-cols-3 gap-6 text-gray-700">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-indigo-100">
              <p className="text-sm font-semibold text-gray-500">
                Resume Score
              </p>
              <p className="text-2xl font-bold text-indigo-700">
                {growthData.metrics.resumeScore}
              </p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-indigo-100">
              <p className="text-sm font-semibold text-gray-500">
                LinkedIn Views
              </p>
              <p className="text-2xl font-bold text-indigo-700">
                {growthData.metrics.linkedinViews}
              </p>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-indigo-100">
              <p className="text-sm font-semibold text-gray-500">
                Recruiter Messages
              </p>
              <p className="text-2xl font-bold text-indigo-700">
                {growthData.metrics.recruiterMessages}
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
